import React, { useEffect, useState, useCallback } from 'react';
import * as api from '../../lib/api';
import Pagination from '../../components/Pagination';
import { Segmented, Tag, TableCard, Th, Td, trClass, UserCell, fmtDateTime, StickyPager } from './ui';

const LIMIT = 50;

const SOURCES = {
  notifications: { label: 'Notificaciones push', fetch: api.getAdminNotificationLogs },
  emails: { label: 'Correos', fetch: api.getAdminEmailLogs },
  errors: { label: 'Errores', fetch: api.getAdminErrorLogs },
};

function NotificationsTable({ items }) {
  return (
    <table className="w-full text-[13px]">
      <thead>
        <tr><Th>Usuario</Th><Th>Tipo</Th><Th>Clave</Th><Th>Fecha</Th></tr>
      </thead>
      <tbody>
        {items.map((it, i) => (
          <tr key={i} className={trClass}>
            <Td className="max-w-[16rem]"><UserCell email={it.user_email} name={it.user_name} userId={it.user_id} /></Td>
            <Td className="text-slate-300 whitespace-nowrap">{it.type}</Td>
            <Td className="text-[11px] text-slate-500 font-mono max-w-xs truncate">{it.key}</Td>
            <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(it.created_at)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EmailsTable({ items }) {
  return (
    <table className="w-full text-[13px]">
      <thead>
        <tr><Th>Usuario</Th><Th>Tipo</Th><Th>Estado</Th><Th>Error</Th><Th>Fecha</Th></tr>
      </thead>
      <tbody>
        {items.map((it, i) => (
          <tr key={i} className={trClass}>
            <Td className="max-w-[16rem]"><UserCell email={it.user_email} name={it.user_name} userId={it.user_id} /></Td>
            <Td className="text-slate-300 whitespace-nowrap">{it.type}</Td>
            <Td><Tag tone={it.status === 'sent' ? 'green' : 'rose'}>{it.status === 'sent' ? 'Enviado' : it.status}</Tag></Td>
            <Td className="text-xs text-slate-500 max-w-xs truncate">{it.error || '—'}</Td>
            <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(it.created_at)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Where an error came from (admin.py ERROR_SOURCES). "admin" = actions
// taken in this panel, not errors.
const ERROR_SOURCES = [
  { key: '', label: 'Todos' },
  { key: 'server', label: 'Servidor' },
  { key: 'app', label: 'App' },
  { key: 'validation', label: 'Validación' },
  { key: 'integration', label: 'Integraciones' },
  { key: 'job', label: 'Tareas' },
  { key: 'admin', label: 'Acciones admin' },
];
const SOURCE_TAG = {
  server: { tone: 'rose', label: 'Servidor' }, app: { tone: 'amber', label: 'App' },
  validation: { tone: 'sky', label: 'Validación' }, integration: { tone: 'violet', label: 'Integración' },
  job: { tone: 'muted', label: 'Tarea' }, admin: { tone: 'green', label: 'Admin' },
};

// One row per error (or per kind of error when grouped); the detail —
// traceback frames, the app's stack, browser — opens below the row.
function ErrorsTable({ items, grouped }) {
  const [open, setOpen] = useState(null);
  return (
    <table className="w-full text-[13px]">
      <thead>
        <tr>
          <Th>Origen</Th><Th>Mensaje</Th>
          {grouped ? <><Th className="text-right">Veces</Th><Th className="text-right">Personas</Th><Th>Última vez</Th></>
            : <><Th>Usuario</Th><Th>Fecha</Th></>}
        </tr>
      </thead>
      <tbody>
        {items.map((it, i) => {
          const tag = SOURCE_TAG[it.source] || { tone: 'muted', label: it.source || it.scope };
          const isOpen = open === i;
          return (
            <React.Fragment key={i}>
              <tr className={`${trClass} cursor-pointer`} onClick={() => setOpen(isOpen ? null : i)}>
                <Td><Tag tone={tag.tone} title={it.scope}>{tag.label}</Tag></Td>
                <Td className="text-slate-300 max-w-xl">
                  <span className="line-clamp-2">{it.message}</span>
                  {it.path && <span className="block text-[11px] text-slate-500 font-mono mt-0.5">{it.path}</span>}
                </Td>
                {grouped ? (
                  <>
                    <Td className="text-right tabular-nums text-slate-200">{it.count}</Td>
                    <Td className="text-right tabular-nums text-slate-400">{it.user_count || '—'}</Td>
                    <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(it.last_seen)}</Td>
                  </>
                ) : (
                  <>
                    <Td className="max-w-[14rem]">{it.user_id ? <UserCell email={it.user_email} name={it.user_name} userId={it.user_id} /> : <span className="text-slate-600">—</span>}</Td>
                    <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(it.ts)}</Td>
                  </>
                )}
              </tr>
              {isOpen && (
                <tr className="border-t border-white/[0.04]">
                  <td colSpan={5} className="px-4 pb-4">
                    <pre className="text-[11px] leading-relaxed text-slate-400 bg-black/30 rounded-xl p-3 whitespace-pre-wrap break-words max-h-80 overflow-auto">
                      {it.detail || 'Sin detalle.'}
                    </pre>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

const TABLES = { notifications: NotificationsTable, emails: EmailsTable, errors: ErrorsTable };

export default function AdminLogs() {
  const [source, setSource] = useState('emails');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [errorSource, setErrorSource] = useState('');
  const [grouped, setGrouped] = useState(true);

  const load = useCallback(async (src, p, errSrc, grp) => {
    setLoading(true);
    try {
      const extra = src === 'errors' ? { source: errSrc || undefined, grouped: grp && errSrc !== 'admin' } : {};
      const res = await SOURCES[src].fetch({ page: p, limit: LIMIT, ...extra });
      setItems(res.items);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(source, page, errorSource, grouped); }, [source, page, errorSource, grouped, load]);

  const handleSourceChange = (src) => {
    setSource(src);
    setPage(1);
  };

  const Table = TABLES[source];
  const isGrouped = source === 'errors' && grouped && errorSource !== 'admin';

  return (
    <div className="space-y-4 pb-2" data-testid="admin-logs">
      <div className="flex items-center justify-between gap-3">
        <Segmented
          options={Object.entries(SOURCES).map(([key, { label }]) => ({ key, label }))}
          value={source}
          onChange={handleSourceChange}
        />
        <p className="text-xs text-slate-500 shrink-0">{total} {isGrouped ? 'tipos de error (30 días)' : 'registros'}</p>
      </div>

      {source === 'errors' && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Segmented options={ERROR_SOURCES} value={errorSource} onChange={(k) => { setErrorSource(k); setPage(1); }} />
          {errorSource !== 'admin' && (
            <Segmented
              options={[{ key: 'grouped', label: 'Agrupados' }, { key: 'all', label: 'Uno por uno' }]}
              value={grouped ? 'grouped' : 'all'}
              onChange={(k) => { setGrouped(k === 'grouped'); setPage(1); }}
            />
          )}
        </div>
      )}

      <TableCard>
        {loading ? (
          <p className="px-4 py-10 text-center text-slate-500 text-sm">Cargando…</p>
        ) : items.length === 0 ? (
          <p className="px-4 py-10 text-center text-slate-500 text-sm">Sin registros.</p>
        ) : (
          <Table items={items} grouped={isGrouped} />
        )}
      </TableCard>

      <StickyPager show={total > LIMIT}>
        <Pagination page={page} pageCount={Math.max(1, Math.ceil(total / LIMIT))} onPageChange={setPage} total={total} pageSize={LIMIT} />
      </StickyPager>
    </div>
  );
}
